/**
 * usePlanList Hook
 *
 * Purpose: Encapsulates media plan list data fetching with server-side search,
 *          debounced input, and refresh capability.
 * Architecture: Loads plans with page_size=250 (backend max) for DataGridPro
 *               virtualization. Server-side search via debounced query param.
 * Dependencies: planningService, useDebounce
 * Author: Development Team
 * Created: 2026-04-01
 */

import { useState, useEffect, useCallback } from 'react';
import planningService, {
  MediaPlan,
  MediaPlanListResponse,
} from '../../../services/planningService';
import { useDebounce } from '../../../utils/ui';

interface UsePlanListReturn {
  plans: MediaPlan[];
  totalCount: number;
  loading: boolean;
  error: string | null;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  refresh: () => void;
}

/**
 * usePlanList — fetches and manages the media plan list
 *
 * Purpose: Provides plan data, loading/error state, and search functionality
 * Input: tenantId — current tenant context (triggers re-fetch on change)
 * Output: UsePlanListReturn — plan data, state, and control functions
 */
export const usePlanList = (tenantId: string): UsePlanListReturn => {
  const [plans, setPlans] = useState<MediaPlan[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');

  const debouncedSetSearch = useDebounce((query: string) => {
    setDebouncedSearch(query);
  }, 300);

  const handleSetSearchQuery = useCallback(
    (query: string) => {
      setSearchQuery(query);
      debouncedSetSearch(query);
    },
    [debouncedSetSearch]
  );

  const fetchPlans = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const response: MediaPlanListResponse = await planningService.getPlans({
        page: 1,
        page_size: 250,
        search: debouncedSearch || undefined,
      });
      setPlans(response.data || []);
      setTotalCount(response.total_count || 0);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      let message: string;
      if (typeof detail === 'string') {
        message = detail;
      } else if (detail && typeof detail === 'object' && 'message' in detail) {
        message = detail.message;
      } else {
        message = err.message || 'Failed to load media plans';
      }
      setError(message);
      setPlans([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [tenantId, debouncedSearch]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const refresh = useCallback(() => {
    fetchPlans();
  }, [fetchPlans]);

  return {
    plans,
    totalCount,
    loading,
    error,
    searchQuery,
    setSearchQuery: handleSetSearchQuery,
    refresh,
  };
};
